import { postUrl } from '@/apiCalls/fetch';
import { setApplicationId, setBorrower } from '@/context/borrower';
import {
    EDisplays,
    EModals,
    setAgreedToTermsAndConditions,
    setModal,
} from '@/context/display';
import { store } from '@/context/store';
import { EJourneyEvent, emitJourneyEvent } from '@/journeyEvents';
import { getProgressPercent } from '@/utils/getProgressPercent';
import {
    ELocalStorageVariables,
    getLocalStorageItem,
    setLocalStorageItem,
} from '@/utils/localStorage';
import { setDemoMode } from '@/utils/setDemoMode';

import {
    IGetBorrowerPostBody,
    IGetBorrowerReturn,
} from './createOrUpdateBorrowerTypes';

export async function createOrUpdateBorrower(): Promise<void> {
    // ***** Redux *****
    const state = store.getState();
    const borrower = { ...state.borrowerReducer }; // Copy borrower to avoid mutating state
    const isLoggedIn = state.userReducer.isLoggedIn;
    const token = getLocalStorageItem(ELocalStorageVariables.AUTH_TOKEN);
    const vehicle = state.borrowerReducer.vehicle;
    const seller = state.borrowerReducer.seller;
    const display = state.displayReducer.display;
    const { storeSelectorOptions } = state.displayReducer.partner;

    const waitForStoreSelector =
        storeSelectorOptions &&
        Array.isArray(storeSelectorOptions) &&
        storeSelectorOptions.length > 0;

    if (!token) {
        console.log('No token found in local storage');
        return;
    }
    if (!isLoggedIn) return;

    // Add progress percentage to borrower
    const currentProgressPercent = getProgressPercent();
    const savedProgressPercent = borrower.application.progressPercent;

    if (
        !savedProgressPercent ||
        currentProgressPercent > savedProgressPercent
    ) {
        borrower.application = {
            ...borrower.application,
            progressPercent: currentProgressPercent,
        }; // Copy application to avoid mutating state
    }
    if (borrower.application.isSubmitted) {
        store.dispatch(setModal(EModals.APPLICATION_IN_PROGRESS));
        return;
    }
    borrower.personalDetails = {
        ...borrower.personalDetails,
        termsAndConditions: state.displayReducer.agreedToTermsAndConditions,
    };

    const sendToDealerSolutions =
        state.displayReducer.partner.sendToDealerSolutions;
    const sendEmailToOnlyCars =
        state.displayReducer.partner.sendEmailToOnlyCars;
    const sendToFintelligence =
        state.displayReducer.partner.sendToFintelligence;

    const mongoPartnerId = state.displayReducer.partner.partnerId;
    const url = '/api/borrower/createOrUpdate';
    const initialJourney = state.displayReducer.initialJourney;

    const body: IGetBorrowerPostBody = {
        borrower,
        partnerId: state.displayReducer.partner.id,
        country: state.displayReducer.partner.country || 'AU',
        sendToDealerSolutions,
        sendEmailToOnlyCars,
        mongoPartnerId,
        sendToFintelligence,
        initialJourney,
        waitForStoreSelector,
    };

    //   const body = {};
    const response = await postUrl<IGetBorrowerReturn>(url, body);
    // ***** Update Redux *****
    const returnedBorrower = response.borrower;
    const returnedFirstName = response.borrower?.personalDetails?.firstName; // To save to local storage
    const returnedLastName = response.borrower?.personalDetails?.lastName;
    const returnedApplication = response.borrower?.application;

    setDemoMode(returnedFirstName ?? '', returnedLastName ?? '');

    // The raw _id is the only value the tracking documents can join back to
    // applications on; the legacy `id` can be a SHA256 hash and stays for the
    // consumers that rely on it. Falls back to the id already in the store: a
    // response without one must not lose an id the session already learned.
    const applicationId =
        returnedApplication?.trackingApplicationId ??
        returnedApplication?.id ??
        state.borrowerReducer.application.applicationId;
    if (applicationId && returnedApplication) {
        /*
         * A lead, for THIS partner.
         *
         * Hung off the application record rather than mobile verification on
         * purpose: the widget is always served from our origin, so the auth
         * token is ours, and a customer who verified on partner A's site can
         * open partner B's widget already authenticated. No verification
         * happens there, so a verification-based trigger would never fire and
         * partner B would never hear about a lead that is new to them.
         *
         * The store held no application id a moment ago and does now, so this
         * is the first application for this partner in this session — which
         * is the same test LoginCode uses to tell a first-timer here from a
         * returning customer.
         */
        if (!state.borrowerReducer.application.applicationId) {
            emitJourneyEvent(EJourneyEvent.LEAD_CAPTURED);
        }
        // setBorrower below replaces the whole slice, so the id has to ride
        // the payload too — dispatched alone it would be wiped again before
        // the next tracking flush could read it.
        returnedApplication.applicationId = applicationId;
        store.dispatch(setApplicationId(applicationId));
    }

    if (returnedFirstName) {
        setLocalStorageItem(
            ELocalStorageVariables.FIRST_NAME,
            returnedFirstName
        );
    }

    // BE does not save these values
    const feAddress = state.borrowerReducer.address;
    returnedBorrower.address = {
        ...feAddress,
        address:
            (returnedBorrower.address.address?.length ?? 0) > 0
                ? returnedBorrower.address.address
                : feAddress.address,
    };
    if (!returnedBorrower.employments) {
        returnedBorrower.employments = {
            currentEmploymentIndex: 0,
            employments: [],
        };
    }
    if (
        returnedBorrower.personalLoans === null ||
        returnedBorrower.personalLoans === undefined
    ) {
        returnedBorrower.personalLoans = {
            personalLoanActiveIndex: 0,
            personalLoans: [],
        };
    }
    if (
        returnedBorrower.creditCards === null ||
        returnedBorrower.creditCards === undefined
    ) {
        returnedBorrower.creditCards = {
            creditCardActiveIndex: 0,
            creditCards: [],
        };
    }
    if (
        returnedBorrower.motorVehicles === null ||
        returnedBorrower.motorVehicles === undefined
    ) {
        returnedBorrower.motorVehicles = {
            motorVehicleActiveIndex: 0,
            motorVehicles: [],
        };
    }
    if (
        returnedBorrower.realEstates === null ||
        returnedBorrower.realEstates === undefined
    ) {
        returnedBorrower.realEstates = {
            realEstateActiveIndex: 0,
            realEstates: [],
        };
    }

    returnedBorrower.address.addressIndex = 0;
    returnedBorrower.employments.currentEmploymentIndex = 0;
    if (
        returnedBorrower.personalLoans === null ||
        returnedBorrower.personalLoans === undefined
    ) {
        returnedBorrower.personalLoans = {
            personalLoanActiveIndex: 0,
            personalLoans: [],
        };
    }

    returnedBorrower.personalLoans.personalLoanActiveIndex = 0;
    returnedBorrower.creditCards.creditCardActiveIndex = 0;
    returnedBorrower.motorVehicles.motorVehicleActiveIndex = 0;
    returnedBorrower.realEstates.realEstateActiveIndex = 0;

    if (returnedBorrower.personalLoans.personalLoans === undefined) {
        returnedBorrower.personalLoans.personalLoans = [];
    }

    if (returnedBorrower.creditCards.creditCards === undefined) {
        returnedBorrower.creditCards.creditCards = [];
    }
    if (returnedBorrower.personalLoans.personalLoans === undefined) {
        returnedBorrower.personalLoans.personalLoans = [];
    }

    if (returnedBorrower.creditCards.creditCards === undefined) {
        returnedBorrower.creditCards.creditCards = [];
    }

    if (returnedFirstName) {
        setLocalStorageItem(
            ELocalStorageVariables.FIRST_NAME,
            returnedFirstName
        );

        setDemoMode(returnedFirstName, returnedLastName ?? '');
    }

    if (returnedBorrower.creditCards.creditCards === undefined) {
        returnedBorrower.creditCards.creditCards = [];
    }

    // BE Would otherwise overwrite FE vehicle / seller set by FE params which is incorrect
    returnedBorrower.seller = seller;
    returnedBorrower.vehicle = vehicle;

    // Preserve the landing-URL attribution (SR-2784). setBorrower replaces the
    // whole slice, and the API only returns what it stored on the first save, so
    // without this a later save would drop tags the session is still carrying.
    returnedBorrower.application.attribution =
        returnedBorrower.application.attribution ??
        state.borrowerReducer.application.attribution;

    // Preserve frontend selfAssessedCreditHistory as it's not saved on backend
    if (state.borrowerReducer.scores.selfAssessedCreditHistory) {
        returnedBorrower.scores = {
            ...returnedBorrower.scores,
            selfAssessedCreditHistory:
                state.borrowerReducer.scores.selfAssessedCreditHistory,
        };
    }

    store.dispatch(
        setAgreedToTermsAndConditions(
            returnedBorrower.personalDetails.termsAndConditions
        )
    );

    store.dispatch(setBorrower(returnedBorrower));

    // When a application is submitted for the first time, they are on the supporting doc screen and call this function to save isSubmitted to the DB, we don't want to force the modal to change because they should see the Congratulations modal instead.
    if (
        returnedBorrower.application.isSubmitted &&
        display !== EDisplays.SUPPORTING_DOCS
    ) {
        store.dispatch(setModal(EModals.APPLICATION_IN_PROGRESS));
    }

    return;
}
